import clsx from 'clsx';
import * as CSS from 'csstype';
import {
  type SyntheticEvent,
  forwardRef,
  Fragment,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { ChevronLeftIcon, ChevronRightIcon } from '@dynatrace/strato-icons';

import { Button } from '../../buttons/button/Button.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useId } from '../../core/hooks/useId.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { useVisualRefreshEnabled } from '../../core/hooks/useVisualRefreshEnabled.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { Divider } from '../../layouts/divider/Divider.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import { useFormFieldContext } from '../forms-core/hooks/useFormFieldContext.js';
import { getI18nValidationState } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';
import type { ValidationState } from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import { ToggleButtonGroupContext } from './contexts/ToggleButtonGroupContext.js';
import { Item } from './Item.js';
import { Prefix } from './Prefix.js';
import * as styles from './styles.sty.js';
import { Suffix } from './Suffix.js';

const COMPONENT_NAME = 'ToggleButtonGroup';

//#region typings
/**
 * Accepted properties for ToggleButtonGroup
 * @public
 */
export type ToggleButtonGroupProps = FormControlProps<
  string,
  (
    value: string,
    event?: SyntheticEvent<HTMLElement | SVGElement>,
  ) => void | never,
  HTMLDivElement
> &
  PropsWithChildren &
  DOMProps &
  StylingProps &
  DataTestId &
  MaskingProps &
  BehaviorTrackingProps & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
    /**
     * The width of the ToggleButtonGroup.
     * @defaultValue 'content'
     */
    width?: 'content' | 'full' | CSS.Property.Width<string & {}>;
  };
//#endregion

const ToggleButtonGroupRoot = forwardRef<
  FormControlRef<HTMLDivElement> | HTMLDivElement | null,
  ToggleButtonGroupProps
>((props, forwardedRef) => {
  const fallbackName = useId();
  const {
    children,
    id: propId,
    disabled: propDisabled,
    required: propRequired,
    readOnly: propReadOnly,
    value,
    defaultValue,
    name = fallbackName,
    _form,
    _messageTarget,
    width = 'content',
    onChange,
    onValidityChange,
    onFocus,
    onBlur,
    className: consumerClassName,
    style: consumerStyle,
    'aria-disabled': propAriaDisabled,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'data-dtrum-allow': dataDtrumAllow,
    'data-dtrum-mask': dataDtrumMask,
    ...remainingProps
  }: ToggleButtonGroupProps = props;
  const {
    id: formControlId,
    disabled: formControlDisabled,
    required: isRequired,
    ariaDisabled,
    readOnly: isReadOnly,
    showError: hasError,
    messageDispatch: messagesContextDispatch,
  } = useFormControl({
    propId,
    propDisabled,
    propRequired,
    propAriaDisabled,
    propReadOnly,
  });

  const intl = useIntl();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const formValidityChangeRef = useRef<
    ((state: ValidationState) => void) | undefined
  >(undefined);
  const _emitValidationState = useValidationStateEmitter((state) =>
    formValidityChangeRef.current?.(state),
  );
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () =>
      getI18nValidationState(
        intl,
        wrapperRef.current?.querySelector('input'),
        'manual',
        formControlId,
      ),
  );
  const { formFieldLabelId } = useFormFieldContext();
  const groupLabelledby =
    ariaLabelledby ?? (ariaLabel === undefined ? formFieldLabelId : undefined);

  const [isOverflowingLeft, setIsOverflowingLeft] = useState(false);
  const [isOverflowingRight, setIsOverflowingRight] = useState(false);

  const isDisabled = propDisabled ?? formControlDisabled;
  const isDisabledOrAriaDisabled = isDisabled || ariaDisabled;

  const [selectedValue, setSelectedValue] = useControllableState({
    defaultValue,
    value,
  });

  const formControl = useInternalFormRegistration({
    id: formControlId,
    name,
    form: _form,
    messageTarget: _messageTarget,
    onValidityChange,
    getValue: () => selectedValue ?? null,
    focus: () => {
      const checkedInput = wrapperRef.current?.querySelector(
        'input:checked',
      ) as HTMLInputElement | null;
      (
        checkedInput ??
        (wrapperRef.current?.querySelector('input') as HTMLInputElement | null)
      )?.focus({ focusVisible: true });
    },
    validateField: ({ reveal, trigger }) => {
      wrapperRef.current
        ?.querySelectorAll('input')
        ?.forEach((input) => input.checkValidity());
      if (reveal) {
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
      const firstInput = wrapperRef.current?.querySelector(
        'input',
      ) as HTMLInputElement | null;
      return Promise.resolve(
        emitValidationState(
          getI18nValidationState(intl, firstInput, trigger, formControlId),
        ),
      );
    },
    resetValidation: () => {
      wrapperRef.current
        ?.querySelectorAll('input')
        ?.forEach((input) => input.setCustomValidity(''));
      messagesContextDispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: false, resetShowError: true },
      });
    },
  });
  formValidityChangeRef.current = formControl.onValidityChange;

  useImperativeHandle<
    FormControlRef<HTMLDivElement> | HTMLDivElement | null,
    FormControlRef<HTMLDivElement> | HTMLDivElement | null
  >(forwardedRef, () => {
    const newRef = {
      element: wrapperRef.current,
      focus() {
        const checkedInput = wrapperRef.current?.querySelector(
          'input:checked',
        ) as HTMLInputElement;
        (
          checkedInput ??
          (wrapperRef.current?.querySelector('input') as HTMLInputElement)
        )?.focus();
      },
      validate() {
        wrapperRef.current
          ?.querySelectorAll('input')
          ?.forEach((i) => i.checkValidity());
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        const firstInput = wrapperRef.current?.querySelector(
          'input',
        ) as HTMLInputElement | null;
        emitValidationState(getI18nValidationState(intl, firstInput, 'manual'));
      },
      // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
      validateAsync: async () => {
        wrapperRef.current
          ?.querySelectorAll('input')
          ?.forEach((i) => i.checkValidity());
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        const firstInput = wrapperRef.current?.querySelector(
          'input',
        ) as HTMLInputElement | null;
        return emitValidationState(
          getI18nValidationState(intl, firstInput, 'manual'),
        );
      },
    };

    if (!wrapperRef.current) {
      return null;
    }

    return new Proxy<HTMLDivElement>(wrapperRef.current, {
      get: function <
        Property extends keyof HTMLDivElement &
          'element' &
          'focus' &
          'validate' &
          'validateAsync',
      >(target: HTMLDivElement, property: Property) {
        if (property in newRef) {
          return newRef[property];
        }
        return target[property];
      },
    });
  }, [messagesContextDispatch, emitValidationState]);

  const handleChange = useCallback(
    (value: string, event?: SyntheticEvent<HTMLElement | SVGElement>) => {
      setSelectedValue?.(value);
      onChange?.(value, event);
    },
    [onChange, setSelectedValue],
  );

  const customWidth =
    width !== 'content' && width !== 'full' ? width : undefined;

  const [slots] = useSlots(children, {
    items: { match: Item, multiple: true },
    prefix: Prefix,
    suffix: Suffix,
  });

  const valueProps = useMemo(
    () => ({
      name,
      form: _form,
      isDisabled,
      ariaDisabled,
      isReadOnly,
      isRequired,
      hasError,
      selectedValue,
      onChange: handleChange,
      onValidityChange: emitValidationState,
    }),
    [
      name,
      _form,
      isDisabled,
      ariaDisabled,
      isReadOnly,
      isRequired,
      hasError,
      selectedValue,
      handleChange,
      emitValidationState,
    ],
  );

  const scrollButtonVariant = useVisualRefreshEnabled()
    ? 'default'
    : 'emphasized';

  const color = hasError && !isDisabledOrAriaDisabled ? 'critical' : 'neutral';

  const { width: containerWidth } = useContainerSize(containerRef);

  const updateOverflow = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, offsetWidth } = containerRef.current;
      const tolerance = 4; // px
      setIsOverflowingLeft(scrollLeft > tolerance);
      setIsOverflowingRight(scrollLeft + offsetWidth < scrollWidth - tolerance);
    }
  }, []);

  const slideTabs = (modifier: 1 | -1): void => {
    const container = containerRef.current;
    if (container) {
      container.scrollBy({
        left: (modifier * container.offsetWidth) / 2,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    updateOverflow();
  }, [containerWidth, updateOverflow]);

  const { containerClassName, ...containerProps } =
    useContainerFocus<HTMLDivElement>({
      onFocus,
      onBlur,
      disabled: isDisabled,
    });

  return (
    <div
      className={clsx(
        containerClassName,
        styles.container,
        {
          [styles.disabledGroup]: isDisabledOrAriaDisabled,
          [styles.error]: hasError,
          [styles.readOnly]: isReadOnly,
          [styles.containerWidths.content]: width === 'content',
          [styles.containerWidths.full]: width === 'full',
        },
        consumerClassName,
      )}
      style={{ maxWidth: customWidth, ...consumerStyle }}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-orientation="horizontal"
      role="radiogroup"
      aria-live="polite"
      aria-label={ariaLabel}
      aria-disabled={ariaDisabled}
      aria-labelledby={groupLabelledby}
      id={formControlId}
      ref={wrapperRef}
      {...containerProps}
      {...remainingProps}
      data-dt-component={COMPONENT_NAME}
    >
      {isOverflowingLeft && (
        <Button
          className={styles.scrollButton}
          style={{ left: '4px', position: 'absolute', top: '4px' }}
          variant={scrollButtonVariant}
          color={color}
          onClick={() => slideTabs(-1)}
          size="condensed"
        >
          <Button.Prefix>
            <ChevronLeftIcon />
          </Button.Prefix>
        </Button>
      )}
      <Flex
        ref={containerRef}
        gap={0}
        onScroll={updateOverflow}
        className={clsx(styles.scrollContainer, {
          [styles.scrollVariants.left]:
            isOverflowingLeft && !isOverflowingRight,
          [styles.scrollVariants.right]:
            !isOverflowingLeft && isOverflowingRight,
          [styles.scrollVariants.both]: isOverflowingLeft && isOverflowingRight,
        })}
      >
        <ToggleButtonGroupContext.Provider value={valueProps}>
          {slots.items?.map((slot, index) => {
            const items = slots.items ?? [];
            const value = slot.props.value;

            const isSelected = value === selectedValue;
            const isLast = index === items.length - 1;
            const nextItem = items[index + 1];
            const nextItemValue = nextItem?.props?.value;

            const nextIsSelected = nextItemValue === selectedValue;
            return (
              <Fragment key={slot.props.value ?? slot.props.id ?? index}>
                {slot}
                {!isLast && (
                  <Divider
                    orientation="vertical"
                    className={clsx(styles.divider, {
                      [styles.hidden]: isSelected || nextIsSelected,
                    })}
                    aria-hidden
                  />
                )}
              </Fragment>
            );
          })}
        </ToggleButtonGroupContext.Provider>
      </Flex>
      {isOverflowingRight && (
        <Button
          className={styles.scrollButton}
          style={{ right: '4px', position: 'absolute', top: '4px ' }}
          variant={scrollButtonVariant}
          color={color}
          onClick={() => slideTabs(1)}
          size="condensed"
        >
          <Button.Suffix>
            <ChevronRightIcon />
          </Button.Suffix>
        </Button>
      )}
    </div>
  );
});

/**
 * `ToggleButtonGroup` lets users toggle between a set of related options, typically with one preselected. It's best for switching between views or a compact group of options with immediate effect.
 * @public
 */
export const ToggleButtonGroup = Object.assign(ToggleButtonGroupRoot, {
  Item,
  Suffix,
  Prefix,
});
